import { Route } from '@angular/router';
import { initialDataResolver } from 'app/app.resolvers';
import { AuthGuard } from 'app/core/auth/guards/auth.guard';
import { NoAuthGuard } from 'app/core/auth/guards/noAuth.guard';
import { LayoutComponent } from 'app/layout/layout.component';
import { environment } from 'environments/environment';

import { OAuthRedirectComponent } from './ui/o-auth-redirect/o-auth-redirect.component';
import { ViewReportShellComponent } from './company/reports/generated-reports/component/view-report-shell/view-report-shell.component';
import { ViewFormComponent } from './company/hyper-forms/components/view-form/view-form.component';
import { PermissionGuard } from './core/auth/guards/permission.guard';
import { legacyDashboardsRedirectGuard } from './core/auth/guards/legacy-dashboards-redirect.guard';
import { legacyFeatureRedirectGuard } from './core/auth/guards/legacy-feature-redirect.guard';
import { rootLandingRedirectGuard } from './core/auth/guards/root-landing-redirect.guard';
import { signedInRedirectGuard } from './core/auth/guards/signed-in-redirect.guard';
import { superAdminDashboardRedirectGuard } from './core/auth/guards/super-admin-dashboard-redirect.guard';
import { RoleGuard } from './core/auth/guards/role-access.guard';
import { CONTENT_PAGE_SLUGS } from './ui/content-page/content-page.model';
import websiteRoutes from './website/website.routes';

export const appRoutes: Route[] = [
    // TEMPORARY diagnostic route, no guard — see dev-composer-preview.component.ts. Delete both
    // before merging.
    {
        path: 'dev-composer-preview',
        loadComponent: () =>
            import('./dev-composer-preview.component').then(
                (m) => m.DevComposerPreviewComponent
            ),
    },

    // Root: signed-out visitors get the marketing site; a live session lands in the workspace
    // (PRODUCT-FINDING-016). The guard always returns a UrlTree; `redirectTo` is a fallback.
    {
        path: '',
        pathMatch: 'full',
        canActivate: [rootLandingRedirectGuard],
        children: [{ path: '', pathMatch: 'full', redirectTo: 'home' }],
    },

    {
        path: 'signed-in-redirect',
        pathMatch: 'full',
        canActivate: [signedInRedirectGuard],
        children: [{ path: '', pathMatch: 'full', redirectTo: 'overview' }],
    },
    { path: 'signed-in-redirect-manager', pathMatch: 'full', redirectTo: '_router' },
    { path: 'login/google/callback', component: OAuthRedirectComponent },
    {
        path: '',
        canActivate: [NoAuthGuard],
        canActivateChild: [NoAuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'confirmation-required',
                loadChildren: () =>
                    import(
                        'app/account/auth/confirmation-required/confirmation-required.routes'
                    ),
            },
            {
                path: 'forgot-password',
                loadChildren: () =>
                    import(
                        'app/account/auth/forgot-password/forgot-password.routes'
                    ),
            },
            {
                path: 'reset-password',
                loadChildren: () =>
                    import(
                        'app/account/auth/reset-password/reset-password.routes'
                    ),
            },
            {
                path: 'sign-in',
                loadChildren: () =>
                    import('app/account/auth/sign-in/sign-in.routes'),
            },
            {
                path: 'sign-up',
                loadChildren: () =>
                    import('app/account/auth/sign-up/sign-up.routes'),
            },
            {
                path: 'otp/:email',
                loadChildren: () =>
                    import('app/account/auth/otp/otp/otp-routes'),
            },
            {
                path: 'signUp-otp/:type/:email',
                loadChildren: () =>
                    import('app/account/auth/otp/otp/otp-routes'),
            },
            {
                path: 'change-password/:email/:code',
                loadChildren: () =>
                    import(
                        'app/account/auth/change-password/change-password.routes'
                    ),
            },
        ],
    },

    // Auth routes for authenticated users
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'sign-out',
                loadChildren: () =>
                    import('app/account/auth/sign-out/sign-out.routes'),
            },
            {
                path: 'unlock-session',
                loadChildren: () =>
                    import(
                        'app/account/auth/unlock-session/unlock-session.routes'
                    ),
            },
        ],
    },

    // Public report viewer only — do not mount full reports.routes here or
    // /reports/generated-reports bypasses the dashboard layout (no sidebar).
    {
        path: 'reports/report/:reportId',
        component: ViewReportShellComponent,
        data: {
            layout: 'empty',
        },
    },
    {
        path: 'reports/report/:reportId/:edit',
        component: ViewReportShellComponent,
        data: {
            layout: 'empty',
        },
    },
    {
        path: 'reports/report-new/:reportId',
        redirectTo: 'reports/report/:reportId',
        pathMatch: 'full',
    },

    // The dashboards viewer, standalone (`layout: 'empty'`), opened in its own browser tab from the
    // list. Same placement and the same reason as the report viewer above it: a dashboard document
    // is a full-bleed application with its own dense chrome, and rendering one beside the app
    // sidebar leaves it a panel inside a page instead of the page.
    //
    // ## This registration MUST stay above the legacy `dashboards` block
    //
    // Angular matches routes IN ORDER, and the admin layout further down carries
    // `{ path: 'dashboards', canActivate: [legacyDashboardsRedirectGuard],
    //    children: [{ path: '**', redirectTo: '/overview' }] }` for pre-migration bookmarks.
    // Registered below that, `/dashboards/view/:dashboardId` would be swallowed by the `**` and
    // every dashboard link would land silently on Overview. `dashboards.routes.spec.ts` proves both
    // orderings against the real router so a move fails a spec rather than a bug report.
    //
    // ## The guards are explicit here, because nothing else applies them
    //
    // Inside the admin layout the viewer inherited `AuthGuard` (`canActivateChild`) and
    // `PermissionGuard`. A top-level route inherits neither, and an unguarded viewer would be a
    // dashboard readable without a session, which contradicts ADR 0021 decision 5 (visibility IS the
    // data grant). `resolvePermissionKey` still answers `dashboards` for this URL: the legacy
    // rewriter leaves `view` alone, so the URL is unchanged and falls through to the map entry.
    //
    // `loadComponent` rather than the eager `component:` the report viewer uses, so the dashboards
    // viewer and its bridge stay out of the initial bundle.
    {
        path: 'dashboards/view/:dashboardId',
        canActivate: [AuthGuard, PermissionGuard],
        loadComponent: () =>
            import(
                'app/company/dashboards/component/view-dashboard-shell/view-dashboard-shell.component'
            ).then(m => m.ViewDashboardShellComponent),
        data: {
            layout: 'empty',
        },
    },
    {
        path: 'form/:formId/:datasourceId',
        component: ViewFormComponent,
        data: {
            layout: 'empty',
        },
    },
    {
        // SAI Messenger: standalone chat app (AI agents + team chat) for user / member / staff.
        // Own shell, own auth screens, admin sessions rejected. See messenger/AGENTS.md.
        path: 'messenger',
        loadChildren: () => import('app/messenger/messenger.routes'),
    },
    {
        path: 'external-custom-agents',
        loadChildren: () =>
            import(
                'app/modules/public/external-agents/external-custom-agents.routes'
            ),
    },

    {
        path: 'chat-with-agent',
        loadChildren: () =>
            import(
                'app/modules/public/external-chat/external-chat.routes'
            ),
    },

    {
        // Public, layout-free content pages. One component; the page is the route data.
        path: 'terms-and-conditions',
        data: { slug: CONTENT_PAGE_SLUGS.terms },
        loadChildren: () => import('app/modules/public/content-page/public-content-page.routes'),
    },
    {
        path: 'privacy-policy',
        data: { slug: CONTENT_PAGE_SLUGS.privacy },
        loadChildren: () => import('app/modules/public/content-page/public-content-page.routes'),
    },

    // Website (standalone, outside Fuse layout) - eager routes avoid ChunkLoadError in dev
    {
        path: '',
        children: websiteRoutes,
    },

    // Admin routes
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver,
        },
        children: [
            {
                path: 'overview',
                canActivate: [superAdminDashboardRedirectGuard],
                loadChildren: () =>
                    import(
                        'app/company/dashboard/project.routes'
                    ),
            },
            {
                path: 'dashboard',
                redirectTo: 'overview',
                pathMatch: 'prefix',
            },

            // The Dashboards module (workspace ADR 0021). Registered ABOVE the legacy block below,
            // which the block's own comment demands: Angular matches in order, so mounted after it
            // every `/dashboards/*` URL would be swallowed by that `**` and land on Overview
            // whatever the guard decided. The guard itself leaves these paths alone (neither the
            // empty segment nor `view` is in LEGACY_DASHBOARDS_CHILD_SEGMENTS), which is also what
            // makes `resolvePermissionKey` gate them on the `dashboards` key rather than deferring.
            {
                path: 'dashboards',
                loadChildren: () => import('app/company/dashboards/dashboards.routes'),
            },

            // Legacy /dashboards/* → flat routes (see legacy-dashboards-redirect.guard.ts).
            // The guard only rewrites KNOWN legacy segments; anything else falls through to the
            // `**` child below and lands on Overview.
            //
            // If a real feature is ever mounted under `/dashboards`, register its routes ABOVE this
            // block. Angular matches in order, so this catch-all would otherwise swallow them
            // whatever the guard decides.
            {
                path: 'dashboards',
                canActivate: [legacyDashboardsRedirectGuard],
                children: [
                    { path: '**', redirectTo: '/overview' },
                ],
            },

            // Legacy feature paths → unified routes
            {
                path: 'my-agents',
                canActivate: [legacyFeatureRedirectGuard],
                children: [{ path: '**', redirectTo: '/ai-agents' }],
            },
            {
                path: 'autonomous-reports',
                canActivate: [legacyFeatureRedirectGuard],
                children: [{ path: '**', redirectTo: '/reports/generated-reports' }],
            },
            {
                path: 'ai-forms',
                canActivate: [legacyFeatureRedirectGuard],
                children: [{ path: '**', redirectTo: '/hyper-forms' }],
            },
            {
                path: 'chat',
                canActivate: [legacyFeatureRedirectGuard],
                children: [
                    {
                        path: '**',
                        redirectTo: environment.production
                            ? '/ai-chat/agent-chat'
                            : '/ai-chat/sai-master',
                    },
                ],
            },
            {
                path: 'chat-ai',
                canActivate: [legacyFeatureRedirectGuard],
                children: [{ path: '**', redirectTo: '/ai-chat/agent-chat' }],
            },

            // Feature modules (flat routes matching src/app/modules/<name>/)
            {
                path: '',
                canActivateChild: [PermissionGuard],
                children: [
                    {
                        path: 'analytics',
                        loadChildren: () =>
                            import(
                                'app/company/analytics/analytics.routes'
                            ),
                    },
                    {
                        path: 'tracker',
                        loadChildren: () =>
                            import(
                                'app/company/tracker/new-tracker.routes'
                            ),
                    },
                    {
                        path: 'procedures',
                        loadChildren: () =>
                            import(
                                'app/company/procedures/procedures.routes'
                            ),
                    },
                    {
                        path: 'add-tracker',
                        loadChildren: () =>
                            import(
                                'app/company/tracker/add-new-tracker.routes'
                            ),
                    },
                    {
                        path: 'ai-chat',
                        loadChildren: () =>
                            import(
                                'app/company/ai-chat/ai-chat.routes'
                            ),
                    },
                    {
                        path: 'data-sources',
                        loadChildren: () =>
                            import(
                                'app/company/data-sources/dataSources.routes'
                            ),
                    },
                    {
                        path: 'ai-agents',
                        loadChildren: () =>
                            import(
                                'app/company/ai-agents/ai-agents.routes'
                            ),
                    },
                    {
                        // Channels is an Organization-Admin-only module.
                        path: 'channels',
                        canActivate: [RoleGuard],
                        data: { roles: ['company'] },
                        loadChildren: () =>
                            import(
                                'app/company/communication-channels/channels.routes'
                            ),
                    },
                    {
                        // Automations is an Organization-Admin-only module: an automation is a
                        // standing grant of authority to act, so it is scoped like Channels.
                        path: 'automations',
                        canActivate: [RoleGuard],
                        data: { roles: ['company'] },
                        loadChildren: () =>
                            import(
                                'app/company/automations/automations.routes'
                            ),
                    },
                    {
                        // Harvester price audits (workspace ADR 0026), gated on the `harvester` key by
                        // `PermissionGuard` via `ROUTE_PERMISSION_MAP['harvester']`. All three roles.
                        path: 'harvester',
                        canActivate: [RoleGuard],
                        data: { roles: ['company', 'manager', 'staff'] },
                        loadChildren: () => import('app/company/harvester/harvester.routes'),
                    },
                    {
                        // CRM. The backend gates both `/v1/crm/` and `/v1/crm-sync/` on the same
                        // `crm` feature key, so `PermissionGuard` covers the whole module via
                        // `ROUTE_PERMISSION_MAP['crm']`.
                        //
                        // All three roles: Leads is the field rep's screen. The narrower scoping for
                        // CRM Settings (§4.4 hides it from `staff`) lives on that child route in
                        // `crm.routes.ts`, because it applies to one branch and not the module.
                        path: 'crm',
                        canActivate: [RoleGuard],
                        data: { roles: ['company', 'manager', 'staff'] },
                        loadChildren: () =>
                            import(
                                'app/company/crm/crm.routes'
                            ),
                    },
                    {
                        // Data Quality is no longer a module of its own. It is a sub-unit of Data
                        // Sources, reached from that page and served at
                        // `/data-sources/data-quality` by `dataSources.routes.ts`, which mirrors
                        // the backend: DQES is mounted BY `data_source` (workspace ADR 0017), not
                        // beside it.
                        //
                        // This entry stays as a redirect so links minted while the page was
                        // top-level still land. It keeps its `cognition` mapping in
                        // `ROUTE_PERMISSION_MAP`, so an unentitled tenant is turned away here
                        // rather than one hop later.
                        path: 'data-quality',
                        redirectTo: 'data-sources/data-quality',
                        pathMatch: 'full',
                    },
                    {
                        // The approvals inbox. No role guard - all three tenant roles, because an
                        // approval addressed to a staff member is theirs to answer - and gated on
                        // the `approvals` permission by `PermissionGuard` through the
                        // `'approvals': 'approvals'` entry in `ROUTE_PERMISSION_MAP`.
                        //
                        // TWO gates still apply and neither substitutes for the other. This one
                        // answers "does the organization hold the module"; entitlement to an
                        // individual row is applied per ITEM inside the backend service against the
                        // SUBJECT's own key, so a page that opens still lists only what the reader
                        // may act on.
                        //
                        // The module gate is what fails silently. `resolvePermissionKey` reads the
                        // FIRST URL segment and the guard answers `true` for an unmapped one, so
                        // losing the map entry does not error - it opens the inbox for every
                        // organization, including those the backend then refuses. Four cases in
                        // `permission.guard.spec.ts` pin it, and the two that matter assert the page
                        // is CLOSED without the key.
                        path: 'approvals',
                        loadChildren: () =>
                            import(
                                'app/company/approvals/approvals.routes'
                            ),
                    },
                    {
                        path: 'reports',
                        loadChildren: () =>
                            import(
                                'app/company/reports/reports.routes'
                            ),
                    },
                    {
                        path: 'activities',
                        loadChildren: () =>
                            import(
                                'app/company/activities/activities.routes'
                            ),
                    },
                    {
                        path: 'activities/:id',
                        loadChildren: () =>
                            import(
                                'app/company/activities/activities.routes'
                            ),
                    },
                    {
                        path: 'artifactory',
                        loadChildren: () =>
                            import(
                                'app/company/artifactory/artifactory.routes'
                            ),
                    },
                    {
                        path: 'custom-agents',
                        loadChildren: () =>
                            import(
                                'app/company/custom-agents/custom-agents.routes'
                            ),
                    },
                    {
                        path: 'teams-chat',
                        loadChildren: () =>
                            import(
                                'app/company/teams-chat/chat/chat.routes'
                            ),
                    },
                    {
                        path: 'tasks',
                        loadChildren: () =>
                            import(
                                'app/company/tasks/tasks.routes'
                            ),
                    },
                    {
                        path: 'admin-custom-agents',
                        loadChildren: () =>
                            import(
                                'app/super-admin/admin-custom-agents/admin-custom-agents.routes'
                            ),
                    },
                    {
                        path: 'admin-agent-settings',
                        canActivate: [AuthGuard, RoleGuard],
                        data: { roles: ['admin'] },
                        loadChildren: () =>
                            import(
                                'app/super-admin/admin-settings/admin-settings.routes'
                            ),
                    },
                    {
                        path: 'admin-api',
                        loadChildren: () =>
                            import(
                                'app/super-admin/admin-api/admin-api.routes'
                            ),
                    },
                    {
                        path: 'hyper-forms',
                        canActivate: [AuthGuard, RoleGuard],
                        loadChildren: () =>
                            import(
                                'app/company/hyper-forms/hyper-forms.routes'
                            ),
                    },
                    {
                        path: 'admin-effort-mapping',
                        canActivate: [AuthGuard, RoleGuard],
                        data: { roles: ['admin'] },
                        loadChildren: () =>
                            import(
                                'app/super-admin/effort-mapping/effort-mapping.routes'
                            ),
                    },
                    {
                        path: 'admin-users',
                        canActivate: [AuthGuard, RoleGuard],
                        data: { roles: ['admin'] },
                        loadChildren: () =>
                            import(
                                'app/super-admin/users/admin-users.routes'
                            ),
                    },
                    {
                        path: 'admin-content-pages',
                        canActivate: [AuthGuard, RoleGuard],
                        data: { roles: ['admin'] },
                        loadChildren: () =>
                            import(
                                'app/super-admin/content-pages/content-pages.routes'
                            ),
                    },
                    {
                        path: 'admin-rag-settings',
                        canActivate: [AuthGuard, RoleGuard],
                        data: { roles: ['admin'] },
                        loadChildren: () =>
                            import(
                                'app/super-admin/rag-chunking-settings/rag-chunking-settings.routes'
                            ),
                    },
                    {
                        path: 'admin-contact',
                        loadChildren: () =>
                            import(
                                'app/super-admin/admin-contact/admin-contact.routes'
                            ),
                    },
                    {
                        path: 'system-instructions',
                        canActivate: [AuthGuard, RoleGuard],
                        data: { roles: ['admin'] },
                        loadChildren: () =>
                            import(
                                'app/super-admin/system-instructions/system-instructions.routes'
                            ),
                    },
                    {
                        path: 'support',
                        loadChildren: () =>
                            import(
                                'app/account/support/support.routes'
                            ),
                    },
                    {
                        // Not `terms`: the marketing site registers `/terms` above this layout and would
                        // always win the match, so the signed-in page was unreachable at that URL.
                        path: 'terms-and-policies',
                        loadChildren: () =>
                            import(
                                'app/account/term/term.routes'
                            ),
                    },
                    {
                        path: 'integrations',
                        loadChildren: () =>
                            import(
                                'app/company/integrations/integrations.routes'
                            ),
                    },
                    {
                        path: 'tutorial',
                        loadChildren: () =>
                            import(
                                'app/account/tutorials/tutorial.routes'
                            ),
                    },
                ],
            },

            {
                path: 'pages',
                children: [
                    // Profile
                    {
                        path: 'profile',
                        loadChildren: () =>
                            import(
                                'app/account/profile/profile.routes'
                            ),
                    },

                    // Member/Staff personal profile
                    {
                        path: 'my-profile',
                        loadChildren: () =>
                            import(
                                'app/account/my-profile/my-profile.routes'
                            ),
                    },

                    // Subscription
                    {
                        path: 'subscription',
                        loadChildren: () =>
                            import(
                                'app/account/subscription/subscription.routes'
                            ),
                    },
                    {
                        path: 'faq',
                        loadChildren: () =>
                            import(
                                'app/account/faq/faq/faq.routes'
                            ),
                    },
                    {
                        path: 'settings',
                        loadChildren: () =>
                            import(
                                'app/account/settings/settings.routes'
                            ),
                    },

                ],
            },
        ],
    },
];
